import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import {
  FiType, FiEdit3, FiSmartphone, FiBookmark, FiCopy, FiCheck, FiRefreshCw, FiX,
} from 'react-icons/fi';
import { AuthContext } from '../../authProvider.mjs';
import signatureService from '../service/signatureService.mjs';
import SignatureCanvas from './SignatureCanvas.mjs';
import { renderTypedSignature, SIGNATURE_FONT, INK } from '../util/renderTypedSignature.mjs';
import { typedNameMatchesProfile, profileName, MAX_TYPED_NAME_LENGTH } from '../util/signerName.mjs';

/**
 * Signing a monthly report (the user, 2026-10-21) - the admin submitting it,
 * the regional director or their named stand-in, the final approver.
 *
 * Four ways in, one result: the saved signature, a typed name drawn in a script
 * face, a drawing made here with the mouse, or a drawing made on a phone. Each
 * becomes the signature payload the action carries to the server:
 *   { saved: true, timeZone }
 *   { method: 'typed', typedName, image, timeZone, remember }
 *   { method: 'drawn', image, timeZone, remember }
 *   { handoffId, timeZone, remember }
 * The server re-checks all of it (backend src/signatures/stageSignature.mjs).
 *
 * The preview is the stamp the report will print: the mark, who, why, when.
 */

const POLL_MS = 2500;

const pad2 = (n) => String(n).padStart(2, '0');

/** "2026-10-20 13:43:18 +03:00" - the signer's clock, as the report prints it. */
export const formatLocalStamp = (d = new Date()) => {
  const offset = -d.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} `
    + `${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())} `
    + `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
};

const browserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

const minutesLeft = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${pad2(s % 60)}`;
};

const TABS = [
  { key: 'saved', label: 'Saved', icon: FiBookmark },
  { key: 'type', label: 'Type', icon: FiType },
  { key: 'draw', label: 'Draw', icon: FiEdit3 },
  { key: 'phone', label: 'Phone', icon: FiSmartphone },
];

const Preview = ({ mark, name, reason }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="border border-gray-200 rounded-lg bg-white p-3 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="sm:w-1/2 h-20 flex items-center justify-center overflow-hidden border-b sm:border-b-0 sm:border-r border-gray-100 pb-2 sm:pb-0 sm:pr-3">
        {mark || <span className="text-sm text-gray-400">Your signature appears here</span>}
      </div>
      <div className="sm:w-1/2 text-xs text-gray-700 leading-relaxed min-w-0">
        <p className="font-semibold text-gray-900 break-words">Electronically signed by {name}</p>
        <p className="break-words">Reason: {reason}</p>
        <p>Date: {formatLocalStamp(now)}</p>
      </div>
    </div>
  );
};

Preview.propTypes = {
  mark: PropTypes.node,
  name: PropTypes.string,
  reason: PropTypes.string,
};

const markImage = (src, alt) => (
  <img src={src} alt={alt} className="max-h-20 max-w-full object-contain" />
);

const SignatureModal = ({
  reportId,
  title = 'Sign this report',
  reason = 'Reviewed and approved this report',
  applyLabel = 'Sign',
  onApply,
  onCancel,
}) => {
  const { user } = useContext(AuthContext) || {};
  const signerName = profileName(user) || user?.email || '';

  const [saved, setSaved] = useState(undefined); // undefined while loading
  const [tab, setTab] = useState(null);
  const [typedName, setTypedName] = useState(signerName);
  const [drawing, setDrawing] = useState(null);
  const [phone, setPhone] = useState({ status: 'idle', handoff: null, image: null, error: '' });
  const [now, setNow] = useState(Date.now());
  const [copied, setCopied] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const drawRef = useRef(null);

  // The saved signature decides where the modal opens.
  useEffect(() => {
    let live = true;
    signatureService.getSaved()
      .then(s => { if (live) { setSaved(s); setTab(s ? 'saved' : 'type'); setRemember(!s); } })
      .catch(() => { if (live) { setSaved(null); setTab('type'); } });
    return () => { live = false; };
  }, []);

  // Esc backs out, as every other dialog in the app does.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, busy]);

  // ---- phone
  const startPhone = useCallback(async () => {
    setPhone({ status: 'starting', handoff: null, image: null, error: '' });
    try {
      const handoff = await signatureService.openHandoff(reportId);
      setPhone({ status: 'waiting', handoff, image: null, error: '' });
    } catch (err) {
      setPhone({ status: 'error', handoff: null, image: null, error: err.message });
    }
  }, [reportId]);

  useEffect(() => {
    if (tab === 'phone' && phone.status === 'idle') startPhone();
  }, [tab, phone.status, startPhone]);

  // Poll only while the Phone tab is showing and the drawing has not arrived.
  useEffect(() => {
    if (tab !== 'phone' || phone.status !== 'waiting' || !phone.handoff) return undefined;
    let live = true;
    let failures = 0;
    let timer = null;
    const tick = async () => {
      try {
        const h = await signatureService.getHandoff(phone.handoff.id);
        failures = 0;
        if (!live) return;
        if (h?.status === 'captured' && h.image) {
          setPhone(p => ({ ...p, status: 'captured', image: h.image }));
          return;
        }
        if (h?.status === 'expired' || h?.status === 'consumed') {
          setPhone(p => ({ ...p, status: 'expired' }));
          return;
        }
      } catch (err) {
        failures += 1;
        if (failures >= 4 && live) {
          setPhone(p => ({ ...p, status: 'error', error: err.message }));
          return;
        }
      }
      if (live) timer = setTimeout(tick, POLL_MS);
    };
    timer = setTimeout(tick, POLL_MS);
    return () => { live = false; clearTimeout(timer); };
  }, [tab, phone.status, phone.handoff]);

  // The countdown under the code.
  useEffect(() => {
    if (phone.status !== 'waiting') return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [phone.status]);
  const msLeft = phone.handoff ? new Date(phone.handoff.expiresAt).getTime() - now : 0;
  useEffect(() => {
    if (phone.status === 'waiting' && phone.handoff && msLeft <= 0) {
      setPhone(p => ({ ...p, status: 'expired' }));
    }
  }, [msLeft, phone.status, phone.handoff]);

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const phoneUrl = phone.handoff ? `${origin}${phone.handoff.path}` : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(phoneUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const forgetSaved = async () => {
    try {
      await signatureService.deleteSaved();
      setSaved(null);
      setTab('type');
      setRemember(true);
    } catch (err) {
      setProblem(err.message);
    }
  };

  // ---- what Apply would sign
  const typedOk = typedNameMatchesProfile(typedName, user) && typedName.trim().length <= MAX_TYPED_NAME_LENGTH;
  const ready = (tab === 'saved' && !!saved)
    || (tab === 'type' && typedOk)
    || (tab === 'draw' && !!drawing)
    || (tab === 'phone' && phone.status === 'captured');

  const mark = (() => {
    if (tab === 'saved' && saved) return markImage(saved.image, 'Your saved signature');
    if (tab === 'type' && typedName.trim()) {
      return (
        <span
          className="whitespace-nowrap leading-none truncate max-w-full"
          style={{ fontFamily: SIGNATURE_FONT, color: INK, fontSize: 34 }}
        >
          {typedName}
        </span>
      );
    }
    if (tab === 'draw' && drawing) return markImage(drawing, 'Your drawn signature');
    if (tab === 'phone' && phone.image) return markImage(phone.image, 'Your signature from your phone');
    return null;
  })();

  const apply = async () => {
    setProblem('');
    const timeZone = browserTimeZone();
    setBusy(true);
    try {
      if (tab === 'saved') {
        onApply({ saved: true, timeZone });
      } else if (tab === 'type') {
        const image = await renderTypedSignature(typedName);
        if (!image) { setProblem('Your typed signature could not be drawn. Try Draw instead.'); return; }
        onApply({ method: 'typed', typedName: typedName.trim(), image, timeZone, remember });
      } else if (tab === 'draw') {
        const image = drawRef.current?.toDataURL();
        if (!image) { setProblem('Draw your signature first.'); return; }
        onApply({ method: 'drawn', image, timeZone, remember });
      } else if (tab === 'phone' && phone.handoff) {
        onApply({ handoffId: phone.handoff.id, timeZone, remember });
      }
    } finally {
      setBusy(false);
    }
  };

  const tabs = TABS.filter(t => t.key !== 'saved' || saved);

  return (
    <div
      className="fixed inset-0 z-[10400] bg-black/50 flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="signature-modal-title"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[calc(100dvh-1.5rem)]">
        <div className="px-5 sm:px-6 py-4 border-b border-gray-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 id="signature-modal-title" className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Your signature is printed on the report with the date and the reason, and recorded in its approval history.
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="p-2 -m-2 text-gray-400 hover:text-gray-600 rounded-lg flex-shrink-0"
            aria-label="Close"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 sm:px-6 py-4 overflow-y-auto">
          {tab === null ? (
            <p className="text-sm text-gray-500 py-8 text-center">Loading...</p>
          ) : (
            <>
              <div role="tablist" aria-label="How to sign" className="flex gap-1 border-b border-gray-200 mb-4 overflow-x-auto overflow-y-hidden">
                {tabs.map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={tab === key}
                    onClick={() => { setTab(key); setProblem(''); }}
                    className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                      tab === key
                        ? 'border-stanford-red text-stanford-red font-medium'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4" aria-hidden="true" />
                    {label}
                  </button>
                ))}
              </div>

              {/* Every panel stays mounted so a drawing survives a look at
                  another tab; hidden ones are display:none. */}
              <div hidden={tab !== 'saved'}>
                {saved && (
                  <div className="text-center py-2">
                    <div className="h-24 flex items-center justify-center">{markImage(saved.image, 'Your saved signature')}</div>
                    <p className="text-xs text-gray-500 mt-2">
                      Your saved signature.{' '}
                      <button onClick={forgetSaved} className="underline hover:text-gray-700">Remove it</button>
                    </p>
                  </div>
                )}
              </div>

              <div hidden={tab !== 'type'}>
                <label htmlFor="typed-signature" className="text-sm font-medium text-gray-700">Type your name</label>
                <input
                  id="typed-signature"
                  value={typedName}
                  maxLength={MAX_TYPED_NAME_LENGTH}
                  onChange={(e) => setTypedName(e.target.value)}
                  autoComplete="name"
                  className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-stanford-red"
                />
                <div className="mt-3 h-20 border-b-2 border-gray-300 flex items-end px-2 overflow-hidden">
                  <span
                    className="whitespace-nowrap leading-none pb-2"
                    style={{ fontFamily: SIGNATURE_FONT, color: INK, fontSize: 'clamp(28px, 7vw, 46px)' }}
                  >
                    {typedName}
                  </span>
                </div>
                <p className={`text-xs mt-2 ${typedName.trim() && !typedOk ? 'text-amber-700' : 'text-gray-500'}`}>
                  Include your first and last name as your profile has them ({signerName}). A middle name is fine.
                </p>
              </div>

              <div hidden={tab !== 'draw'}>
                <div className="relative border border-gray-300 rounded-lg bg-gray-50">
                  <SignatureCanvas
                    ref={drawRef}
                    height={180}
                    label="Draw your signature"
                    onChange={(has) => setDrawing(has ? drawRef.current?.toDataURL() || null : null)}
                  />
                  <div className="absolute left-4 right-4 bottom-9 border-b border-gray-300 pointer-events-none" />
                  <span className="absolute left-4 bottom-3 text-xs text-gray-400 pointer-events-none">
                    Sign above the line with your mouse or trackpad
                  </span>
                  {drawing && (
                    <button
                      onClick={() => drawRef.current?.clear()}
                      className="absolute top-2 right-2 text-xs px-2 py-1 bg-white border border-gray-300 rounded-md text-gray-600 hover:bg-gray-100"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div hidden={tab !== 'phone'}>
                {phone.status === 'captured' ? (
                  <div className="text-center py-2">
                    <div className="h-24 flex items-center justify-center">{markImage(phone.image, 'Your signature from your phone')}</div>
                    <p className="text-sm text-emerald-700 mt-2 flex items-center justify-center gap-1">
                      <FiCheck aria-hidden="true" /> Received from your phone
                    </p>
                    <button onClick={startPhone} className="text-xs text-gray-500 underline mt-1 hover:text-gray-700">
                      Sign again on your phone
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
                    <div className="w-[176px] h-[176px] flex-shrink-0 flex items-center justify-center border border-gray-200 rounded-lg bg-white p-2">
                      {phone.status === 'waiting' && phoneUrl && (
                        <QRCodeSVG value={phoneUrl} size={156} level="M" title="Scan to sign on your phone" />
                      )}
                      {(phone.status === 'starting' || phone.status === 'idle') && (
                        <span className="text-xs text-gray-400">Preparing code...</span>
                      )}
                      {(phone.status === 'expired' || phone.status === 'error') && (
                        <button
                          onClick={startPhone}
                          className="flex flex-col items-center gap-2 text-sm text-stanford-red hover:underline"
                        >
                          <FiRefreshCw className="w-5 h-5" aria-hidden="true" />
                          Get a new code
                        </button>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 min-w-0 flex-1 w-full">
                      <ol className="list-decimal pl-5 space-y-1">
                        <li>Scan the code with your phone&apos;s camera.</li>
                        <li>Sign with your finger and tap Done.</li>
                        <li>Your signature appears here. Then press {applyLabel}.</li>
                      </ol>
                      {phone.status === 'waiting' && phone.handoff && (
                        <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3">
                          <p className="text-xs text-gray-500">Can&apos;t scan? On your phone, open</p>
                          <p className="font-medium text-gray-900 break-all">{origin}/sign</p>
                          <p className="text-xs text-gray-500 mt-1">and enter this code:</p>
                          <p className="font-mono text-xl tracking-widest text-gray-900 mt-0.5">{phone.handoff.code}</p>
                          <button
                            onClick={copyLink}
                            className="mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                          >
                            {copied ? <FiCheck aria-hidden="true" /> : <FiCopy aria-hidden="true" />}
                            {copied ? 'Link copied' : 'Copy link to send to your phone'}
                          </button>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-3" aria-live="polite">
                        {phone.status === 'waiting' && `Waiting for your phone. The code expires in ${minutesLeft(msLeft)}.`}
                        {phone.status === 'expired' && 'That code has expired.'}
                        {phone.status === 'error' && (phone.error || 'Something went wrong.')}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-5">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Preview</p>
                <Preview mark={mark} name={signerName} reason={reason} />
              </div>

              {tab !== 'saved' && (
                <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="rounded border-gray-300 text-stanford-red focus:ring-stanford-red"
                  />
                  {saved ? 'Replace my saved signature with this one' : 'Save this signature for next time'}
                </label>
              )}

              {problem && <p className="mt-3 text-sm text-red-700">{problem}</p>}
            </>
          )}
        </div>

        <div className="px-5 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-xl flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-xs text-gray-500 flex-1 min-w-0">
            By pressing {applyLabel} you agree this is your electronic signature and that you have reviewed this report.
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 flex-shrink-0">
            <button
              onClick={onCancel}
              disabled={busy}
              className="min-h-[44px] sm:min-h-0 px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              onClick={apply}
              disabled={!ready || busy}
              className="min-h-[44px] sm:min-h-0 px-5 py-2 text-sm bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red disabled:opacity-50 font-medium"
            >
              {busy ? 'Signing...' : applyLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

SignatureModal.propTypes = {
  reportId: PropTypes.string.isRequired,
  title: PropTypes.string,
  reason: PropTypes.string,
  applyLabel: PropTypes.string,
  onApply: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default SignatureModal;
